/**
 * The first time inside (Figma 10 · First run 01–02).
 *
 * - FirstBrandScreen: an Owner whose workspace has no brand gets this page in
 *   place of an empty Home. The website, when given, is handed to the
 *   Universe's own first run, which asks for it anyway.
 * - WelcomeLine: a teammate's first landing gets one line (M05): it rises
 *   600 ms after the page is ready, stays 6 s (longer while hovered or focused),
 *   and shows once per person and workspace on this device.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Hand, X } from "lucide-react";
import { useCreateBrand } from "@/hooks/useCreateBrand";
import { SPRING_SMOOTH } from "@/lib/motion";
import { PublicShell } from "./PublicShell";
import { Column, GiButton, GiField, Greeting, Head, Notice, QuietAction } from "./parts";
import { SignOutConfirm } from "./SessionDialogs";
import { readAuthFailure } from "./authApi";

const WEBSITE_KEY = "co-first-brand-website";

/** The website typed on the first-brand page, once, for the Universe's first run. */
export function takeFirstBrandWebsite(): string {
  try {
    const value = sessionStorage.getItem(WEBSITE_KEY) ?? "";
    sessionStorage.removeItem(WEBSITE_KEY);
    return value;
  } catch {
    return "";
  }
}

export function FirstBrandScreen({ firstName, onSignOut }: { firstName: string; onSignOut: () => void }) {
  const navigate = useNavigate();
  const createBrand = useCreateBrand();
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const add = () => {
    if (name.trim().length < 2) return setNameError("Add the brand’s name.");
    setNameError(null);
    setFailure(null);
    createBrand.mutate(name.trim(), {
      onSuccess: () => {
        try {
          if (website.trim()) sessionStorage.setItem(WEBSITE_KEY, website.trim());
        } catch {
          /* the Universe just asks again */
        }
        navigate("/dashboard/brand-universe", { replace: true });
      },
      onError: (error) =>
        setFailure(
          readAuthFailure(error).kind === "offline"
            ? "Creo cannot be reached. Check your connection; what you typed stays."
            : "The brand was not added. Try again in a moment.",
        ),
    });
  };

  return (
    <PublicShell
      still="sitting"
      title="Add your first brand"
      topAction={<QuietAction onClick={() => setSigningOut(true)}>Sign out</QuietAction>}
    >
      <Column>
        <Greeting />
        <Head
          title="Add your first brand"
          sub={`Welcome${firstName ? `, ${firstName}` : ""}. A brand is a client you make content for. More brands and your team can come later.`}
        />
        {failure ? <Notice tone="attention">{failure}</Notice> : null}
        <form
          className="flex w-full flex-col gap-6"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <div className="flex w-full flex-col gap-4">
            <GiField
              label="Brand name"
              placeholder="The client’s name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={nameError}
              readOnly={createBrand.isPending}
              autoFocus
            />
            <GiField
              label="Website"
              placeholder="brand.com"
              inputMode="url"
              autoComplete="url"
              hint="Optional."
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              readOnly={createBrand.isPending}
            />
          </div>
          <GiButton type="submit" loading={createBrand.isPending}>
            Add brand
          </GiButton>
        </form>
        <QuietAction to="/dashboard/settings?page=people">Invite your team first</QuietAction>
      </Column>
      <SignOutConfirm isOpen={signingOut} onOpenChange={setSigningOut} onConfirm={onSignOut} />
    </PublicShell>
  );
}

/* ------------------------------------------------------------ the welcome line */

const SEEN_PREFIX = "co-welcomed:";
const WELCOME_WINDOW_DAYS = 14;

/** Whether this membership still gets its one welcome on this device (plan D12). */
export function shouldWelcome(userId: string, organizationId: string, joinedAt: string | undefined): boolean {
  if (!joinedAt) return false;
  if (Date.now() - new Date(joinedAt).getTime() > WELCOME_WINDOW_DAYS * 86_400_000) return false;
  try {
    return localStorage.getItem(`${SEEN_PREFIX}${userId}:${organizationId}`) === null;
  } catch {
    return false;
  }
}

function markWelcomed(userId: string, organizationId: string) {
  try {
    localStorage.setItem(`${SEEN_PREFIX}${userId}:${organizationId}`, new Date().toISOString());
  } catch {
    /* storage off: it may show once more */
  }
}

export function WelcomeLine({
  userId,
  organizationId,
  title,
  body,
}: {
  userId: string;
  organizationId: string;
  title: string;
  body: string;
}) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(false);
  const [held, setHeld] = useState(false);
  const remaining = useRef(6000);
  const startedAt = useRef(0);

  // 600 ms after the page is ready; seen as soon as it shows.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setShown(true);
      markWelcomed(userId, organizationId);
    }, 600);
    return () => window.clearTimeout(id);
  }, [userId, organizationId]);

  // 6 s on screen; hovering or focusing it pauses the clock.
  useEffect(() => {
    if (!shown || held) return;
    startedAt.current = Date.now();
    const id = window.setTimeout(() => setShown(false), remaining.current);
    return () => {
      window.clearTimeout(id);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    };
  }, [shown, held]);

  return (
    <AnimatePresence>
      {shown ? (
        <motion.div
          role="status"
          initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, transition: { duration: reduced ? 0.16 : 0.18 } }}
          transition={reduced ? { duration: 0.16 } : SPRING_SMOOTH}
          onPointerEnter={() => setHeld(true)}
          onPointerLeave={() => setHeld(false)}
          onFocus={() => setHeld(true)}
          onBlur={() => setHeld(false)}
          className="co-glass-chrome fixed bottom-8 left-1/2 z-[var(--z-index-toast,60)] flex w-[min(380px,calc(100vw-24px))] items-start gap-3 rounded-panel px-3 py-3.5"
          style={{ x: "-50%" }}
        >
          <Hand aria-hidden className="mt-px size-4 shrink-0 text-co-text" strokeWidth={1.75} />
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-[13px] font-semibold leading-[18px] text-co-text">{title}</span>
            <span className="text-[12px] leading-[19px] text-co-muted">{body}</span>
          </span>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setShown(false)}
            className="grid size-7 shrink-0 place-items-center rounded-full text-co-muted transition-colors hover:bg-co-hover hover:text-co-text"
          >
            <X aria-hidden className="size-3.5" />
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
